import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import StoreIcon from "@mui/icons-material/Store";
import CategoryIcon from "@mui/icons-material/Category";
import FoodBankIcon from "@mui/icons-material/FoodBank";
import LogoutIcon from "@mui/icons-material/Logout";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import BuildIcon from "@mui/icons-material/Build";
import ArticleIcon from "@mui/icons-material/Article";
import DisplaySettingsIcon from "@mui/icons-material/DisplaySettings";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import AddIcon from "@mui/icons-material/Add";
import PercentIcon from "@mui/icons-material/Percent";
import PaymentIcon from "@mui/icons-material/Payment";
import GridViewIcon from "@mui/icons-material/GridView";

import ChangeStoreComponent from "./changeStore.component";
import "@fontsource/roboto/300.css";

import { useStateContext } from "../context/ContextProvider";
import { Navigate, useNavigate, useLocation } from "react-router-dom";
import { Outlet } from "react-router-dom";
import axiosClientSevan from "../axiosClientSevan";
import moment from "moment";

const drawerWidth = 240;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

export default function DrawerComponent() {
  const theme = useTheme();
  const [open, setOpen] = React.useState(true);
  const { token, store, setToken, setStore } = useStateContext();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  React.useEffect(() => {
    if (store) {
      const sevanPayload = {
        shop_code: JSON.parse(store)?.store_bill_id,
        service_id: 301,
        platform_name: "SOFTECH",
      };

      axiosClientSevan
        .post("api/shop/service/exist", sevanPayload)
        .then((res) => {
          const serviceWithExpDates = res.data.data.service_type_data;
          const hasAServiceActive = serviceWithExpDates.filter(
            (obj) => obj.service_type_expriration_Date != "-"
          );

          const currentDate = moment().format('DD-MM-YYYY');
          let expirationDate = hasAServiceActive[0].service_type_expriration_Date;
          const momentDate1 = moment(currentDate, 'DD-MM-YYYY');
          const momentDate2 = moment(expirationDate, 'DD-MM-YYYY');

          if (momentDate1.isAfter(momentDate2)) {
            setToken("");
          }
        });
    }
  }, [store]);

  if (!token) {
    return <Navigate to="/login" />;
  }

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar position="fixed" open={open}>
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            onClick={handleDrawerOpen}
            edge="start"
            sx={{
              marginRight: 5,
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div">
            {JSON.parse(store)?.name != null
              ? `You are in the store: ${JSON.parse(store)?.name}`
              : ""}
          </Typography>
          <Box sx={{ width: "20px" }} />
          <ChangeStoreComponent />
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "rtl" ? (
              <ChevronRightIcon />
            ) : (
              <ChevronLeftIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          {[
            {
              name: "Products",
              icon: (
                <FoodBankIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/products",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/products"),
            },
            {
              name: "Categories",
              icon: (
                <CategoryIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/categories",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/categories"),
            },
            {
              name: "Groups",
              icon: (
                <WorkspacesIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/groups",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/groups"),
            },
            {
              name: "Preview",
              icon: (
                <GridViewIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/view-grid",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/view-grid"),
            },
          ].map((obj, index) => (
            <ListItem key={obj.name} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  backgroundColor:
                    obj.pathname === location.pathname ? "lightgrey" : "white",
                }}
                onClick={obj.handleClick}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {obj.icon}
                </ListItemIcon>
                <ListItemText
                  primary={obj.name}
                  sx={{
                    opacity: open ? 1 : 0,
                    color:
                      location.pathname === "/insert-details"
                        ? "grey"
                        : "black",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <List>
          {[
            {
              name: "Platforms",
              icon: (
                <IntegrationInstructionsIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/platforms",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/platforms"),
            },
            {
              name: "Payment methods",
              icon: (
                <PaymentIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/payments",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/payments"),
            },
            {
              name: "Vats",
              icon: (
                <PercentIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/vats",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/vats"),
            },
            {
              name: "Users",
              icon: (
                <ManageAccountsIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/users",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/users"),
            },
          ].map((obj, index) => (
            <ListItem key={obj.name} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  backgroundColor:
                    obj.pathname === location.pathname ? "lightgrey" : "white",
                }}
                onClick={obj.handleClick}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {obj.icon}
                </ListItemIcon>
                <ListItemText
                  primary={obj.name}
                  sx={{
                    opacity: open ? 1 : 0,
                    color:
                      location.pathname === "/insert-details"
                        ? "grey"
                        : "black",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <List>
          {[
            {
              name: "Reports",
              icon: (
                <ArticleIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/reports",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/reports"),
            },
            {
              name: "Store details",
              icon: (
                <StoreIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/store-details",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/store-details"),
            },
            {
              name: "System settings",
              icon: (
                <BuildIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/system-settings",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/system-settings"),
            },
          ].map((obj, index) => (
            <ListItem key={obj.name} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  backgroundColor:
                    obj.pathname === location.pathname ? "lightgrey" : "white",
                }}
                onClick={obj.handleClick}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {obj.icon}
                </ListItemIcon>
                <ListItemText
                  primary={obj.name}
                  sx={{
                    opacity: open ? 1 : 0,
                    color:
                      location.pathname === "/insert-details" &&
                      obj.name !== "Logout"
                        ? "grey"
                        : "black",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        <Divider />
        <List>
          {[
            // {
            //   name: "Customer profile",
            //   icon: (
            //     <AccountCircleIcon
            //       color={
            //         location.pathname === "/insert-details"
            //           ? "disabled"
            //           : "inherit"
            //       }
            //     />
            //   ),
            //   pathname: "/profile",
            //   handleClick: location.pathname === "/insert-details" ? null : () => navigate("/profile")
            // },
            {
              name: "Add Company",
              icon: (
                <AddIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/company",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/company"),
            },
            {
              name: "Add Store",
              icon: (
                <AddIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/store",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/store"),
            },
            {
              name: "License details",
              icon: (
                <DisplaySettingsIcon
                  color={
                    location.pathname === "/insert-details"
                      ? "disabled"
                      : "inherit"
                  }
                />
              ),
              pathname: "/license",
              handleClick:
                location.pathname === "/insert-details"
                  ? null
                  : () => navigate("/license"),
            },
            {
              name: "Logout",
              icon: <LogoutIcon />,
              handleClick: () => {
                setToken(null);
                setStore(null);
              },
            },
          ].map((obj, index) => (
            <ListItem key={obj.name} disablePadding sx={{ display: "block" }}>
              <ListItemButton
                sx={{
                  minHeight: 48,
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  backgroundColor:
                    obj.pathname === location.pathname ? "lightgrey" : "white",
                }}
                onClick={obj.handleClick}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : "auto",
                    justifyContent: "center",
                  }}
                >
                  {obj.icon}
                </ListItemIcon>
                <ListItemText
                  primary={obj.name}
                  sx={{
                    opacity: open ? 1 : 0,
                    color:
                      location.pathname === "/insert-details" &&
                      obj.name !== "Logout"
                        ? "grey"
                        : "black",
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Drawer>
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <DrawerHeader />
        <Outlet />
      </Box>
    </Box>
  );
}
